import Category from "@/models/Category";
import Tag from "@/models/Tag";
import Task from "@/models/Task";
import { StorageService } from "@/services/StorageService";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

export interface DataProviderInterface {
  tasks: Task[];
  categories: Category[];
  tags: Tag[];
  addTask: (task: Task) => void;
  updateTask: (task: Task) => void;
  deleteTask: (taskId: string) => void;
  addCategory: (category: Category) => void;
  updateCategory: (category: Category) => void;
  deleteCategory: (categoryId: string) => void;
  addTag: (tag: Tag) => void;
  updateTag: (tag: Tag) => void;
  deleteTag: (tagId: string) => void;
}

const DataProviderContext = createContext({});

export const useDataProvider = () => {
  const context = useContext(DataProviderContext);
  if (!context) {
    throw new Error(
      "useDataProvider must be used within a DataProvider component",
    );
  }
  return context as DataProviderInterface;
};

interface Data {
  tasks: Task[];
  categories: Category[];
  tags: Tag[];
}

export const DataProvider: React.FC<{
  children?: React.ReactNode;
  storageService: StorageService;
}> = ({ children, storageService }) => {
  const [data, _setData] = useState<Data>({
    tasks: [],
    categories: [],
    tags: [],
  });

  useEffect(() => {
    const fetchData = async () => {
      const [fetchedTasks, fetchedCategories, fetchedTags] = await Promise.all([
        storageService.getTasks(),
        storageService.getCategories(),
        storageService.getTags(),
      ]);
      _setData({
        tasks: fetchedTasks,
        categories: fetchedCategories,
        tags: fetchedTags,
      });
    };
    fetchData();
  }, [storageService]);

  const updateData = useCallback(
    async (data: Data) => {
      await Promise.all([
        storageService.saveTasks(data.tasks),
        storageService.saveCategories(data.categories),
        storageService.saveTags(data.tags),
      ]);
      _setData(data);
    },
    [storageService],
  );

  return (
    <DataProviderContext.Provider
      value={{
        tasks: data.tasks,
        categories: data.categories,
        tags: data.tags,
        addTask: (task: Task) => {
          const newTasks = [...data.tasks, task];
          updateData({ ...data, tasks: newTasks });
        },
        updateTask: (task: Task) => {
          const newTasks = data.tasks.map((t) => (t.id === task.id ? task : t));
          updateData({ ...data, tasks: newTasks });
        },
        deleteTask: (taskId: string) => {
          const newTasks = data.tasks.filter((t) => t.id !== taskId);
          updateData({ ...data, tasks: newTasks });
        },
        addCategory: (category: Category) => {
          const newCategories = [...data.categories, category];
          updateData({ ...data, categories: newCategories });
        },
        updateCategory: (category: Category) => {
          const newCategories = data.categories.map((c) =>
            c.id === category.id ? category : c,
          );
          updateData({ ...data, categories: newCategories });
        },
        deleteCategory: (categoryId: string) => {
          const newCategories = data.categories.filter(
            (c) => c.id !== categoryId,
          );
          updateData({ ...data, categories: newCategories });
        },
        addTag: (tag: Tag) => {
          const newTags = [...data.tags, tag];
          updateData({ ...data, tags: newTags });
        },
        updateTag: (tag: Tag) => {
          const newTags = data.tags.map((t) => (t.id === tag.id ? tag : t));
          updateData({ ...data, tags: newTags });
        },
        deleteTag: (tagId: string) => {
          const newTags = data.tags.filter((t) => t.id !== tagId);
          updateData({ ...data, tags: newTags });
        },
      }}
    >
      {children}
    </DataProviderContext.Provider>
  );
};
